import React, { useState, useMemo, useCallback } from 'react';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { StructuredProject } from '@mb/lib';
import { handleSystemErrorReport } from '@mb/lib/utils';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  Text,
  Select,
  SelectTrigger,
  SelectItem,
  SelectValue,
  SelectContent,
  Input,
  createContactRepMessage,
  AssignedRepSelect,
} from 'UILibrary';
// TODO: refactor. The MiniPDP should not import from DigitalSampling, but rather from the common reuseable modules
import { useRequestQuoteTranslations } from '../../../../../../../../MaterialBank/DigitalSampling/view/frontend/app/components/RequestQuote/useRequestQuoteTranslations';
import {
  useCreateOrderForPricingAndQuote,
  useCreateOrderForSeePricing,
} from '../../api/digitalSamples';
import { LearnMoreButton as ActionButton } from '../../containers/MiniPDP/MiniPdpContent.styled';
import {
  handleQuoteDrawerProjectSelected,
  handleRequestQuoteComplete,
  handleRequestQuoteCTAClick,
} from '../../helpers/analytics';
import { definedTranslation } from '../../i18n';
import { setSnackbarInfo } from '../../redux/sliceCreators/commonSlice';
import { SnackbarStatusEnum } from '../../types';
import { ProductDetails } from '../../types/product';
import { CustomTooltip } from '../CustomTooltip';

interface TooltipContentProps {
  projects: StructuredProject[];
  currentProjectData: StructuredProject;
  material: ProductDetails;
  onClose?: () => void;
}

const TooltipContent: React.FC<TooltipContentProps> = ({
  projects,
  currentProjectData,
  material,
  onClose,
}) => {
  const intl = useIntl();
  const { createOrderForPricingAndQuote } = useCreateOrderForPricingAndQuote();
  const { projects: allProjects } = useProjectsProvider();
  const { isProjectDataComplete } = useValidateProjectData();
  const { UNIT_OF_MEASURE_VALUE_MAP } = useRequestQuoteTranslations();

  const [purchasingQuantity, setPurchasingQuantity] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<string | undefined>(
    undefined,
  );
  const [assignedRepId, setAssignedRepId] = useState<string | undefined>(
    undefined,
  );
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const dispatch = useDispatch();
  const mappedProjects = useMemo(
    () =>
      projects.map((project: StructuredProject) => ({
        label: project.label,
        value: String(project.id),
      })),
    [projects],
  );

  const pluralUnitOfMeasure =
    UNIT_OF_MEASURE_VALUE_MAP[material.originalMaterial.unitsOfMeasure ?? ''];

  const handleSubmitForm = async () => {
    setIsRequestLoading(true);
    try {
      await createOrderForPricingAndQuote(
        material.originalMaterial.sku,
        material.sku,
        parseInt(purchasingQuantity),
        pluralUnitOfMeasure,
      );
      await createContactRepMessage({
        product_id: parseInt(material.entityId),
        project_id:
          parseInt(currentProjectId || '') || currentProjectData?.id || 0,
        manufacturer: material.originalMaterial.manufacturer || '',
        rep_id: assignedRepId || '',
        product_name: material.nameLabel,
        message_source: 'Request quote',
        message_type: 'quote',
        quote: {
          purchasing_qty: purchasingQuantity,
          unit_of_measure: pluralUnitOfMeasure,
        },
      });
      setShowSuccessMessage(true);

      const projectId = currentProjectId || String(currentProjectData?.id);
      const projectName =
        projects.find((project) => String(project.id) === projectId)?.label ||
        null;
      handleRequestQuoteComplete(
        material.originalMaterial,
        projectId,
        projectName,
        String(pluralUnitOfMeasure),
        parseInt(purchasingQuantity),
      );
    } catch (error) {
      dispatch(
        setSnackbarInfo({
          isOpen: true,
          status: SnackbarStatusEnum.DANGER,
          message: error.message || 'Some error occurred',
        }),
      );
      handleSystemErrorReport(
        `Error in PricingTooltipButton: ${error.message}`,
        'handleSubmitForm',
      );
    }
    setIsRequestLoading(false);
  };
  const handleClose = () => {
    onClose?.();
  };
  const handleGoToInbox = () => {
    window.location.href = '/inbox';
    onClose?.();
  };
  const handleProjectChange = (id: string) => {
    const project = allProjects[id];

    if (!project) {
      // TODO: toast error?
      return;
    }

    const projectLabel =
      projects.find((p) => String(p.id) === id)?.label || null;

    const onProjectSelected = () => {
      setCurrentProjectId(id);
      handleQuoteDrawerProjectSelected(
        material.originalMaterial,
        id,
        projectLabel,
      );
    };

    // Complete any missing project data
    if (!isProjectDataComplete(project)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: project.externalId,
        successCallback: onProjectSelected,
      });

      return;
    }

    onProjectSelected();
  };

  const isButtonDisalbed =
    !(currentProjectId || String(currentProjectData?.id)) ||
    !purchasingQuantity ||
    isRequestLoading ||
    assignedRepId === '' ||
    assignedRepId === undefined;

  const formContent = (
    <React.Fragment>
      <div className="flex flex-col gap-3 pb-[12px] mb-4 border-b border-[#F2F2F2]">
        <Text>
          <FormattedMessage {...definedTranslation.pricingTooltip.subtitle} />
        </Text>
        <div className="flex w-full pt-4 pb-1 border-t border-[#F2F2F2]">
          <AssignedRepSelect
            productEntityId={material.entityId}
            onAssignedRepChange={(assignedRepId: string) =>
              setAssignedRepId(assignedRepId)
            }
          />
        </div>
        <Select
          value={currentProjectId || String(currentProjectData?.id)}
          onChange={handleProjectChange}
          direction="bottom-start"
          options={mappedProjects}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={intl.formatMessage({
                ...definedTranslation.pricingTooltip.selectPlaceholder,
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {mappedProjects.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative">
          <Input
            min={0}
            type="number"
            placeholder={intl.formatMessage({
              ...definedTranslation.pricingTooltip.inputPlaceholder,
            })}
            value={purchasingQuantity}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setPurchasingQuantity(e.target.value);
            }}
          />
          {pluralUnitOfMeasure && (
            <div className="flex bg-[#F5F5F6] rounded-full h-full items-center pr-5 absolute top-0 right-0">
              {pluralUnitOfMeasure}
            </div>
          )}
        </div>
      </div>
      <ActionButton
        fullWidth
        size="large"
        variant="contained"
        color="primary"
        onClick={handleSubmitForm}
        disabled={isButtonDisalbed}
      >
        <FormattedMessage
          {...definedTranslation.pricingTooltip.submitButtonLabel}
        />
      </ActionButton>
    </React.Fragment>
  );

  const succesContent = (
    <div className="w-full">
      <div className="flex flex-col pb-4 mb-4 border-b border-[#F2F2F2]">
        <Text variant="H3" className="pt-1 pb-1 text-[18px]">
          <FormattedMessage
            {...definedTranslation.pricingTooltip.successMessage.title}
          />
        </Text>
        <Text>
          <FormattedMessage
            {...definedTranslation.pricingTooltip.successMessage.subtitle}
          />
        </Text>
      </div>
      <div className="flex gap-3 mt-4">
        <div className="w-1/2">
          <ActionButton
            fullWidth
            size="large"
            variant="contained"
            color="secondary"
            onClick={handleClose}
          >
            <FormattedMessage
              {...definedTranslation.pricingTooltip.successMessage
                .closeButtonLabel}
            />
          </ActionButton>
        </div>
        <div className="w-1/2">
          <ActionButton
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            onClick={handleGoToInbox}
          >
            <FormattedMessage
              {...definedTranslation.pricingTooltip.successMessage
                .submitButtonLabel}
            />
          </ActionButton>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <Text variant="H2">
        <FormattedMessage {...definedTranslation.pricingTooltip.priceLabel} />:{' '}
        {material.originalMaterial.priceRange || '-'}
      </Text>
      {showSuccessMessage ? succesContent : formContent}
    </div>
  );
};

interface PricingTooltipButtonProps {
  projects: StructuredProject[];
  currentProjectData: StructuredProject;
  material: ProductDetails;
}

// Button with Tooltip to show pricing and quote form
const PricingTooltipButton: React.FC<PricingTooltipButtonProps> = ({
  projects,
  currentProjectData,
  material,
}) => {
  const { createOrderForSeePricing } = useCreateOrderForSeePricing();

  const [showTooltip, setShowTooltip] = useState(false);
  const handleClickAway = useCallback(() => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  }, [showTooltip]);
  const toggleTooltip = () => {
    if (!showTooltip) {
      handleRequestQuoteCTAClick(
        material.originalMaterial,
        String(currentProjectData?.id),
        currentProjectData?.label,
      );

      // createOrderForSeePricing is handling the case when no project is selected,
      // the project data is incomplete or the project selection dialog should be triggered
      createOrderForSeePricing({
        itemSku: material.originalMaterial.sku,
        successCallback: () => {
          setShowTooltip(!showTooltip);
        },
        continueFlowCallback: () => {
          // open the tooltip after the dialog is closed
          setShowTooltip(true);
        },
      });

      return;
    }

    setShowTooltip(!showTooltip);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div>
        <CustomTooltip
          disableFocusListener
          disableHoverListener
          disableTouchListener
          slotProps={{
            popper: {
              disablePortal: true,
            },
          }}
          open={showTooltip}
          title={
            <TooltipContent
              projects={projects}
              currentProjectData={currentProjectData}
              material={material}
              onClose={toggleTooltip}
            />
          }
          placement="bottom-end"
        >
          <span style={{ marginLeft: '12px' }}>
            <ActionButton
              size="large"
              variant="contained"
              color="primary"
              onClick={toggleTooltip}
            >
              <FormattedMessage
                {...definedTranslation.pricingTooltip.actionButtonLabel}
              />
            </ActionButton>
          </span>
        </CustomTooltip>
      </div>
    </ClickAwayListener>
  );
};

export { PricingTooltipButton };
